<template>
  <div
    class="smart-img"
    :class="[`is-${mode}`, { 'is-loading': status === 'loading' }]"
    :style="{ aspectRatio: ratio }"
  >
    <div v-if="status === 'loading'" class="smart-img__skeleton ui-skeleton" aria-hidden="true"></div>

    <!--
      Latar buram dari gambar yang sama, dipakai saat bentuk gambar tidak
      cocok dengan bentuk kartu.

      Kartu kini berbentuk potret 3:4, jadi poster acara (umumnya A4, sekitar
      1:1.414) hampir pas dan ditampilkan penuh tanpa pemotongan berarti.
      Latar buram ini menjadi jaring pengaman untuk gambar berbentuk lain —
      misalnya foto lanskap yang diunggah panitia — supaya tidak ada bagian
      penting yang terpotong dan tidak muncul bidang kosong di kiri-kanan.
    -->
    <img
      v-if="src && mode === 'contain'"
      :src="src"
      alt=""
      aria-hidden="true"
      class="smart-img__backdrop"
    />

    <img
      v-if="src"
      :src="src"
      :alt="alt"
      :loading="eager ? 'eager' : 'lazy'"
      :decoding="eager ? 'sync' : 'async'"
      class="smart-img__el"
      @load="onLoad"
      @error="handleError"
    />

    <div v-if="tahap > 0 && label" class="smart-img__overlay" aria-hidden="true">
      <span class="smart-img__label">{{ label }}</span>
    </div>
  </div>
</template>

<script>
import { mediaUrl, fallbackImage, placeholderImage } from '@/utils/media';

/**
 * Gambar yang selalu menampilkan sesuatu.
 *
 * Rantai cadangan bertingkat:
 *   tahap 0 — berkas asli dari basis data
 *   tahap 1 — foto suasana gelanggang (aset lokal, sudah dikompres)
 *   tahap 2 — SVG bergradasi merek (data-URI, mustahil gagal)
 *
 * Diperlukan karena di komputer lokal folder storage/app/public kosong:
 * berkas gambar hanya ada di server produksi. Tanpa rantai ini setiap kartu
 * turnamen menampilkan ikon rusak.
 *
 * Penanda `tahap` mencegah putaran tak berujung — tanpa itu, gambar
 * pengganti yang ikut gagal akan memicu handler error berulang kali.
 *
 * Mode tampilan ditentukan otomatis dari bentuk gambar setelah dimuat.
 * Gambar yang bentuknya jauh berbeda dari wadahnya memakai mode "contain"
 * dengan latar buram; sisanya memakai "cover".
 *
 * Dengan wadah potret 3:4 (rasio 0,75) dan poster A4 (rasio 0,707), selisih
 * keduanya kecil sehingga poster tampil "cover" — memenuhi kartu dengan
 * pemotongan yang nyaris tidak terlihat.
 */
export default {
  name: 'SmartImage',

  props: {
    source: { type: String, default: '' },
    alt: { type: String, default: '' },
    label: { type: String, default: '' },
    ratio: { type: String, default: '3 / 4' },
    eager: { type: Boolean, default: false },
    /** 'auto' | 'cover' | 'contain' */
    fit: { type: String, default: 'auto' },
  },

  data() {
    return { src: null, status: 'loading', tahap: 0, mode: 'cover' };
  },

  watch: {
    source: {
      immediate: true,
      handler() {
        this.status = 'loading';
        this.mode = this.fit === 'contain' ? 'contain' : 'cover';

        const asli = mediaUrl(this.source);

        if (asli) {
          this.tahap = 0;
          this.src = asli;
        } else {
          // Tidak ada jalur sama sekali — langsung ke foto pengganti, tanpa
          // menunggu satu permintaan jaringan gagal lebih dulu.
          this.tahap = 1;
          this.src = fallbackImage(this.label || this.alt);
        }
      },
    },
  },

  methods: {
    onLoad(e) {
      this.status = 'loaded';

      if (this.fit !== 'auto') return;

      // Bandingkan rasio gambar dengan rasio wadah. Gambar yang jauh lebih
      // tinggi daripada wadahnya akan kehilangan bagian penting bila dipotong.
      const img = e.target;
      const rasioGambar = img.naturalWidth / img.naturalHeight;
      const [a, b] = String(this.ratio).split('/').map((n) => parseFloat(n.trim()));
      const rasioWadah = b ? a / b : 16 / 9;

      this.mode = rasioGambar < rasioWadah * 0.82 ? 'contain' : 'cover';
    },

    handleError() {
      if (this.tahap === 0) {
        this.tahap = 1;
        this.src = fallbackImage(this.label || this.alt);
      } else if (this.tahap === 1) {
        this.tahap = 2;
        this.src = placeholderImage(this.label);
      } else {
        this.status = 'loaded';
      }
    },
  },
};
</script>

<style scoped>
.smart-img {
  position: relative;
  display: block;
  width: 100%;
  overflow: hidden;
  background-color: var(--bg-sunken);
}

.smart-img__skeleton { position: absolute; inset: 0; z-index: 2; }

.smart-img__backdrop {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  /* Diperbesar sedikit supaya tepi buram tidak menyisakan garis kosong */
  transform: scale(1.15);
  filter: blur(26px) saturate(130%) brightness(0.62);
  pointer-events: none;
}

.smart-img__el {
  position: relative;
  display: block;
  width: 100%;
  height: 100%;
  opacity: 0;
  transition: opacity 500ms var(--ease-out-quart), transform 700ms var(--ease-out-expo);
}

.smart-img.is-cover .smart-img__el   { object-fit: cover; }
.smart-img.is-contain .smart-img__el { object-fit: contain; }

.smart-img:not(.is-loading) .smart-img__el { opacity: 1; }

.smart-img__overlay {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: flex-end;
  padding: var(--space-4);
  background: linear-gradient(
    to top,
    rgb(16 15 14 / 0.86) 0%,
    rgb(16 15 14 / 0.4) 42%,
    transparent 72%
  );
  pointer-events: none;
}

.smart-img__label {
  font-family: var(--font-display);
  font-size: var(--text-sm);
  font-weight: 700;
  line-height: 1.2;
  text-transform: uppercase;
  letter-spacing: 0.02em;
  color: #fff;
  text-shadow: 0 1px 3px rgb(0 0 0 / 0.5);
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
</style>
